import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { memo, useCallback, useMemo, useState } from 'react';
import rafSchedule from 'raf-schd';
import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import { Card as SmartCard } from '@atlaskit/smart-card';
import { registerCard, registerRemoveOverlay } from '../pm-plugins/actions';
import { AwarenessWrapper } from '../ui/AwarenessWrapper';
var InlineCard = function InlineCard(_ref) {
  var node = _ref.node,
    cardContext = _ref.cardContext,
    actionOptions = _ref.actionOptions,
    showServerActions = _ref.showServerActions,
    useAlternativePreloader = _ref.useAlternativePreloader,
    view = _ref.view,
    getPos = _ref.getPos,
    isOverlayEnabled = _ref.isOverlayEnabled,
    isPulseEnabled = _ref.isPulseEnabled,
    pluginInjectionApi = _ref.pluginInjectionApi,
    _ref$isSelected = _ref.isSelected,
    isSelected = _ref$isSelected === void 0 ? false : _ref$isSelected;
  var _node$attrs = node.attrs,
    url = _node$attrs.url,
    data = _node$attrs.data;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isHovered = _useState2[0],
    setIsHovered = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isInserted = _useState4[0],
    setIsInserted = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isResolvedViewRendered = _useState6[0],
    setIsResolvedViewRendered = _useState6[1];
  var scrollContainer = useMemo(function () {
    return findOverflowScrollParent(view.dom) || undefined;
  }, [view.dom]);
  var onResolve = useCallback(function (data) {
    if (!getPos || typeof getPos === 'boolean') {
      return;
    }
    var title = data.title,
      url = data.url;
    // don't dispatch immediately since we might be in the middle of
    // rendering a nodeview
    rafSchedule(function () {
      // prosemirror-bump-fix
      var pos = getPos();
      if (typeof pos !== 'number') {
        return;
      }
      var tr = view.state.tr;
      registerCard({
        title: title,
        url: url,
        pos: pos
      })(tr);
      registerRemoveOverlay(function () {
        return setIsInserted(false);
      })(tr);
      view.dispatch(tr);
    })();
    if (title) {
      setIsResolvedViewRendered(true);
    }
  }, [view, getPos]);
  var onError = useCallback(function (data) {
    var url = data.url,
      err = data.err;
    if (err) {
      throw err;
    }
    onResolve({
      url: url
    });
  }, [onResolve]);

  // Begin Upgrade Awareness related code
  var markMostRecentlyInsertedLink = useCallback(function (isLinkMostRecentlyInserted) {
    if (isOverlayEnabled) {
      setIsInserted(isLinkMostRecentlyInserted);
    }
  }, [isOverlayEnabled]);
  var setOverlayHoveredStyles = useCallback(function (isHovered) {
    if (isOverlayEnabled) {
      setIsHovered(isHovered);
    }
  }, [isOverlayEnabled]);
  //  End Upgrade Awareness related code

  var innerCard = useMemo(function () {
    return /*#__PURE__*/React.createElement(SmartCard, {
      key: url,
      url: url,
      data: data,
      appearance: "inline",
      onClick: function onClick() {},
      container: scrollContainer,
      onResolve: onResolve,
      onError: onError,
      inlinePreloaderStyle: useAlternativePreloader ? 'on-right-without-skeleton' : undefined,
      actionOptions: actionOptions,
      showServerActions: showServerActions,
      isHovered: isHovered
    });
  }, [data, isHovered, onError, onResolve, scrollContainer, url, useAlternativePreloader, actionOptions, showServerActions]);
  var card = useMemo(function () {
    return isOverlayEnabled || isPulseEnabled ? /*#__PURE__*/React.createElement(AwarenessWrapper, {
      isOverlayEnabled: isOverlayEnabled,
      isPulseEnabled: isPulseEnabled,
      cardContext: cardContext,
      getPos: getPos,
      isHovered: isHovered,
      isInserted: isInserted,
      url: url,
      isSelected: isSelected,
      isResolvedViewRendered: isResolvedViewRendered,
      markMostRecentlyInsertedLink: markMostRecentlyInsertedLink,
      pluginInjectionApi: pluginInjectionApi,
      setOverlayHoveredStyles: setOverlayHoveredStyles
    }, innerCard) : /*#__PURE__*/React.createElement("span", {
      className: "card"
    }, innerCard);
  }, [cardContext, getPos, innerCard, isHovered, isInserted, isOverlayEnabled, isPulseEnabled, isResolvedViewRendered, isSelected, markMostRecentlyInsertedLink, pluginInjectionApi, setOverlayHoveredStyles, url]);

  // [WS-2307]: we only render card wrapped into a Provider when the value is ready,
  // otherwise if we got data, we can render the card directly since it doesn't need the Provider
  return cardContext && cardContext.value ? /*#__PURE__*/React.createElement(cardContext.Provider, {
    value: cardContext.value
  }, card) : data ? card : null;
};
export var InlineCardWithAwareness = /*#__PURE__*/memo(InlineCard);